import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Lock, Key, Save, CheckCircle, AlertCircle, Eye, EyeOff, Users, Plus, Trash2, FlaskConical } from 'lucide-react';
import { settingsApi, teamMembersApi } from '../../services/api';
import { TeamMember } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

interface SettingsData {
  _id: string;
  name: string;
  email: string;
  role: 'admin' | 'client';
  phone: string;
  isActive: boolean;
  hasOpenaiApiKey: boolean;
  hasGeminiApiKey: boolean;
  createdAt: string;
  updatedAt: string;
}

const Settings: React.FC = () => {
  const { isAdmin, isTeamMember } = useAuth();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState<SettingsData | null>(null);

  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showPw, setShowPw] = useState({ current: false, new: false, confirm: false });
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});

  const [geminiKey, setGeminiKey] = useState('');
  const [openaiKey, setOpenaiKey] = useState('');
  const [showKeys, setShowKeys] = useState({ gemini: false, openai: false });
  const [geminiError, setGeminiError] = useState<string | undefined>();
  const [openaiError, setOpenaiError] = useState<string | undefined>();
  const [geminiLoading, setGeminiLoading] = useState(false);
  const [openaiLoading, setOpenaiLoading] = useState(false);

  type TestResult = { ok: true } | { ok: false; message: string } | null;
  const [geminiTestResult, setGeminiTestResult] = useState<TestResult>(null);
  const [openaiTestResult, setOpenaiTestResult] = useState<TestResult>(null);
  const [geminiTesting, setGeminiTesting] = useState(false);
  const [openaiTesting, setOpenaiTesting] = useState(false);

  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', email: '', password: '', confirmPassword: '' });

  useEffect(() => {
    fetchSettings();
    if (!isAdmin && !isTeamMember) fetchTeamMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await settingsApi.getSettings();
      if (response.data.success && response.data.data) {
        setSettings(response.data.data);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = () => {
    const errors: Record<string, string> = {};
    if (!passwordData.currentPassword) errors.currentPassword = 'Please enter your current password';
    if (!passwordData.newPassword) errors.newPassword = 'Please enter a new password';
    else if (passwordData.newPassword.length < 6) errors.newPassword = 'Password must be at least 6 characters';
    if (!passwordData.confirmPassword) errors.confirmPassword = 'Please confirm your new password';
    else if (passwordData.newPassword !== passwordData.confirmPassword) errors.confirmPassword = 'The two passwords do not match!';
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePassword()) return;
    setLoading(true);
    try {
      const response = await settingsApi.updatePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      if (response.data.success) {
        toast.success('Password updated successfully');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setPasswordErrors({});
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    setTeamLoading(true);
    try {
      const resp = await teamMembersApi.list();
      setTeamMembers((resp.data as any)?.data || []);
    } catch {
      // ignore
    } finally {
      setTeamLoading(false);
    }
  };

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMember.name.trim()) { toast.error('Name is required'); return; }
    if (!newMember.email.trim()) { toast.error('Email is required'); return; }
    if (newMember.password && newMember.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (newMember.password !== newMember.confirmPassword) { toast.error('Passwords do not match'); return; }
    try {
      const resp = await teamMembersApi.create({
        name: newMember.name.trim(),
        email: newMember.email.trim(),
        ...(newMember.password && { password: newMember.password }),
      });
      const member = (resp.data as any)?.data;
      if (member) setTeamMembers(prev => [...prev, member]);
      setNewMember({ name: '', email: '', password: '', confirmPassword: '' });
      setShowAddForm(false);
      toast.success('Team member added');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to add team member');
    }
  };

  const handleRemoveMember = async (id: string) => {
    try {
      await teamMembersApi.remove(id);
      setTeamMembers(prev => prev.filter(m => m._id !== id));
      toast.success('Team member removed');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to remove team member');
    }
  };

  const handleGeminiSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!geminiKey.trim()) { setGeminiError('Gemini API key is required'); return; }
    setGeminiLoading(true);
    try {
      const response = await settingsApi.updateGeminiApiKey(geminiKey.trim());
      if (response.data.success) {
        toast.success('Gemini API key updated successfully');
        fetchSettings();
        setGeminiKey('');
        setGeminiError(undefined);
      }
    } catch (error: any) {
      const keyErr = error.response?.data?.data?.geminiApiKey;
      setGeminiError(keyErr || error.response?.data?.message || 'Failed to update Gemini API key');
      toast.error('Gemini API key is invalid');
    } finally {
      setGeminiLoading(false);
    }
  };

  const handleOpenaiSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!openaiKey.trim()) { setOpenaiError('OpenAI API key is required'); return; }
    setOpenaiLoading(true);
    try {
      const response = await settingsApi.updateOpenaiApiKey(openaiKey.trim());
      if (response.data.success) {
        toast.success('OpenAI API key updated successfully');
        fetchSettings();
        setOpenaiKey('');
        setOpenaiError(undefined);
      }
    } catch (error: any) {
      const keyErr = error.response?.data?.data?.openaiApiKey;
      setOpenaiError(keyErr || error.response?.data?.message || 'Failed to update OpenAI API key');
      toast.error('OpenAI API key is invalid');
    } finally {
      setOpenaiLoading(false);
    }
  };

  const handleTestGemini = async () => {
    setGeminiTesting(true);
    setGeminiTestResult(null);
    try {
      const res = await settingsApi.testGeminiApiKey();
      if (res.data.success) {
        setGeminiTestResult({ ok: true });
      } else {
        const d = res.data.data;
        const prefix = d?.providerStatus ? `[${d.providerStatus}] ` : '';
        setGeminiTestResult({ ok: false, message: prefix + (d?.providerMessage || res.data.message || 'Test failed') });
      }
    } catch (err: any) {
      setGeminiTestResult({ ok: false, message: err.response?.data?.message || err.message || 'Request failed' });
    } finally {
      setGeminiTesting(false);
    }
  };

  const handleTestOpenai = async () => {
    setOpenaiTesting(true);
    setOpenaiTestResult(null);
    try {
      const res = await settingsApi.testOpenaiApiKey();
      if (res.data.success) {
        setOpenaiTestResult({ ok: true });
      } else {
        const d = res.data.data;
        const prefix = d?.providerStatus ? `[${d.providerStatus}] ` : '';
        setOpenaiTestResult({ ok: false, message: prefix + (d?.providerMessage || res.data.message || 'Test failed') });
      }
    } catch (err: any) {
      setOpenaiTestResult({ ok: false, message: err.response?.data?.message || err.message || 'Request failed' });
    } finally {
      setOpenaiTesting(false);
    }
  };

  if (isAdmin) {
    return (
      <div className="p-6 space-y-6">
        <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-blue-800">Admin Settings</p>
              <p className="text-sm text-blue-700 mt-1">
                API key management is only available for client accounts. As an admin, you can manage
                client accounts and their settings from the App Users page.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>

      {/* Account Information */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Account Information</h2>
        {loading && !settings ? (
          <p className="text-sm text-gray-500">Loading...</p>
        ) : settings ? (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Name</p>
              <p className="text-sm text-gray-900">{settings.name}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Email</p>
              <p className="text-sm text-gray-900">{settings.email}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Role</p>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                settings.role === 'admin'
                  ? 'bg-red-50 text-red-700 border border-red-200'
                  : 'bg-blue-50 text-blue-700 border border-blue-200'
              }`}>
                {settings.role}
              </span>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Status</p>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                settings.isActive
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {settings.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        ) : null}
      </div>

      {/* Change Password */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Lock className="w-4 h-4 text-gray-500" />
          <h2 className="text-base font-semibold text-gray-900">Change Password</h2>
        </div>
        <form onSubmit={handlePasswordUpdate} className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
            <div className="relative">
              <input
                type={showPw.current ? 'text' : 'password'}
                value={passwordData.currentPassword}
                onChange={e => setPasswordData(p => ({ ...p, currentPassword: e.target.value }))}
                placeholder="Enter current password"
                className={`w-full px-3 py-2 pr-10 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  passwordErrors.currentPassword ? 'border-red-400' : 'border-gray-300'
                }`}
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowPw(s => ({ ...s, current: !s.current }))}
              >
                {showPw.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {passwordErrors.currentPassword && (
              <p className="text-xs text-red-500 mt-1">{passwordErrors.currentPassword}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <div className="relative">
              <input
                type={showPw.new ? 'text' : 'password'}
                value={passwordData.newPassword}
                onChange={e => setPasswordData(p => ({ ...p, newPassword: e.target.value }))}
                placeholder="Enter new password (minimum 6 characters)"
                className={`w-full px-3 py-2 pr-10 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  passwordErrors.newPassword ? 'border-red-400' : 'border-gray-300'
                }`}
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowPw(s => ({ ...s, new: !s.new }))}
              >
                {showPw.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {passwordErrors.newPassword && (
              <p className="text-xs text-red-500 mt-1">{passwordErrors.newPassword}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
            <div className="relative">
              <input
                type={showPw.confirm ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={e => setPasswordData(p => ({ ...p, confirmPassword: e.target.value }))}
                placeholder="Confirm new password"
                className={`w-full px-3 py-2 pr-10 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  passwordErrors.confirmPassword ? 'border-red-400' : 'border-gray-300'
                }`}
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowPw(s => ({ ...s, confirm: !s.confirm }))}
              >
                {showPw.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {passwordErrors.confirmPassword && (
              <p className="text-xs text-red-500 mt-1">{passwordErrors.confirmPassword}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            Update Password
          </button>
        </form>
      </div>

      {/* API Keys — hidden for team members */}
      {!isTeamMember && <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Key className="w-4 h-4 text-gray-500" />
          <h2 className="text-base font-semibold text-gray-900">API Keys</h2>
        </div>

        <div className="flex items-start gap-3 bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 shrink-0" />
          <div className="text-sm text-yellow-800">
            <p className="font-medium mb-1">API Keys Required</p>
            <p className="mb-2">Your Knowledge Assistant requires both API keys to function. Please configure:</p>
            <ul className="list-disc pl-4 space-y-1">
              <li><strong>Gemini API Key:</strong> Required for AI chat responses and embeddings</li>
              <li><strong>OpenAI API Key:</strong> Required for AI chat responses and fallback</li>
            </ul>
            <p className="mt-2 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              Both API keys must be set for your Knowledge Assistant to work.
            </p>
          </div>
        </div>

        {settings && (
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="border border-gray-200 rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2">
                {settings.hasGeminiApiKey ? (
                  <>
                    <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                    <span className="text-sm font-medium text-gray-700">Gemini API Key:</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                      Configured
                    </span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-4 h-4 text-orange-400 shrink-0" />
                    <span className="text-sm font-medium text-gray-700">Gemini API Key:</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
                      Not Set
                    </span>
                  </>
                )}
              </div>
              {settings.hasGeminiApiKey && (
                <div className="space-y-1">
                  <button
                    type="button"
                    onClick={handleTestGemini}
                    disabled={geminiTesting}
                    className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded border border-gray-300 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FlaskConical className="w-3.5 h-3.5" />
                    {geminiTesting ? 'Testing...' : 'Test Key'}
                  </button>
                  {geminiTestResult && (
                    geminiTestResult.ok
                      ? <p className="text-xs text-green-600 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 shrink-0" /> Valid</p>
                      : <p className="text-xs text-red-600 flex items-start gap-1"><AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" /><span>{geminiTestResult.message}</span></p>
                  )}
                </div>
              )}
            </div>
            <div className="border border-gray-200 rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2">
                {settings.hasOpenaiApiKey ? (
                  <>
                    <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                    <span className="text-sm font-medium text-gray-700">OpenAI API Key:</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                      Configured
                    </span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-4 h-4 text-orange-400 shrink-0" />
                    <span className="text-sm font-medium text-gray-700">OpenAI API Key:</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
                      Not Set
                    </span>
                  </>
                )}
              </div>
              {settings.hasOpenaiApiKey && (
                <div className="space-y-1">
                  <button
                    type="button"
                    onClick={handleTestOpenai}
                    disabled={openaiTesting}
                    className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded border border-gray-300 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FlaskConical className="w-3.5 h-3.5" />
                    {openaiTesting ? 'Testing...' : 'Test Key'}
                  </button>
                  {openaiTestResult && (
                    openaiTestResult.ok
                      ? <p className="text-xs text-green-600 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 shrink-0" /> Valid</p>
                      : <p className="text-xs text-red-600 flex items-start gap-1"><AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" /><span>{openaiTestResult.message}</span></p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="space-y-4 max-w-md">
          {/* Gemini API Key */}
          <form onSubmit={handleGeminiSave} className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              <span className="text-red-500 mr-0.5">*</span>Gemini API Key
              <span className="ml-1.5 text-xs font-normal text-gray-500">(required)</span>
            </label>
            <div className="relative">
              <input
                type={showKeys.gemini ? 'text' : 'password'}
                value={geminiKey}
                onChange={e => { setGeminiKey(e.target.value); if (geminiError) setGeminiError(undefined); }}
                placeholder="Enter Gemini API key"
                autoComplete="new-password"
                className={`w-full px-3 py-2 pr-10 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${geminiError ? 'border-red-400' : 'border-gray-300'}`}
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowKeys(s => ({ ...s, gemini: !s.gemini }))}
              >
                {showKeys.gemini ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {geminiError && <p className="text-xs text-red-500">{geminiError}</p>}
            <p className="text-xs text-gray-500">
              Required for AI chat responses and embeddings. Get your key from Google AI Studio.
            </p>
            <button
              type="submit"
              disabled={geminiLoading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {geminiLoading ? 'Saving...' : 'Save Gemini Key'}
            </button>
          </form>

          <hr className="border-gray-200" />

          {/* OpenAI API Key */}
          <form onSubmit={handleOpenaiSave} className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              <span className="text-red-500 mr-0.5">*</span>OpenAI API Key
              <span className="ml-1.5 text-xs font-normal text-gray-500">(required)</span>
            </label>
            <div className="relative">
              <input
                type={showKeys.openai ? 'text' : 'password'}
                value={openaiKey}
                onChange={e => { setOpenaiKey(e.target.value); if (openaiError) setOpenaiError(undefined); }}
                placeholder="Enter OpenAI API key"
                autoComplete="new-password"
                className={`w-full px-3 py-2 pr-10 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${openaiError ? 'border-red-400' : 'border-gray-300'}`}
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowKeys(s => ({ ...s, openai: !s.openai }))}
              >
                {showKeys.openai ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {openaiError && <p className="text-xs text-red-500">{openaiError}</p>}
            <p className="text-xs text-gray-500">
              Required for AI chat responses. Get your key from OpenAI Platform.
            </p>
            <button
              type="submit"
              disabled={openaiLoading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {openaiLoading ? 'Saving...' : 'Save OpenAI Key'}
            </button>
          </form>

          <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">Security Note</p>
              <p className="text-xs mt-0.5">
                Your API keys are stored securely and will be used only for your Knowledge Assistant requests.
                Keys are encrypted and never exposed in API responses.
              </p>
            </div>
          </div>
        </div>
      </div>}

      {/* Team Members — hidden for team members */}
      {!isTeamMember && <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-500" />
            <h2 className="text-base font-semibold text-gray-900">Team Members</h2>
          </div>
          <button
            onClick={() => setShowAddForm(v => !v)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Member
          </button>
        </div>

        <p className="text-xs text-gray-500 mb-4">
          Team members can be assigned to feedback items. Add your internal team here.
        </p>

        {showAddForm && (
          <form onSubmit={handleAddMember} className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                <input
                  type="text"
                  value={newMember.name}
                  onChange={e => setNewMember(m => ({ ...m, name: e.target.value }))}
                  placeholder="Full name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input
                  type="email"
                  value={newMember.email}
                  onChange={e => setNewMember(m => ({ ...m, email: e.target.value }))}
                  placeholder="email@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Password <span className="text-gray-400">(optional)</span></label>
                <input
                  type="password"
                  value={newMember.password}
                  onChange={e => setNewMember(m => ({ ...m, password: e.target.value }))}
                  placeholder="Min. 6 characters"
                  autoComplete="new-password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Confirm Password</label>
                <input
                  type="password"
                  value={newMember.confirmPassword}
                  onChange={e => setNewMember(m => ({ ...m, confirmPassword: e.target.value }))}
                  placeholder="Repeat password"
                  autoComplete="new-password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => { setShowAddForm(false); setNewMember({ name: '', email: '', password: '', confirmPassword: '' }); }}
                className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {teamLoading ? (
          <p className="text-sm text-gray-500">Loading team members…</p>
        ) : teamMembers.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-gray-200 rounded-lg">
            <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No team members yet</p>
            <p className="text-xs text-gray-400 mt-1">Add team members to assign them to feedback items</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
            {teamMembers.map(member => (
              <div key={member._id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-semibold shrink-0">
                    {member.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{member.name}</p>
                    <p className="text-xs text-gray-500">{member.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveMember(member._id)}
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                  title="Remove member"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>}
    </div>
  );
};

export default Settings;
